import React, { useEffect, useState } from 'react';
import { View, Modal } from 'react-native';
import { translate } from '../../../services/translate';
import FormInput from '../../../components/FormInput';
import { WindowInformation } from '../../../services/window-information';
import { createStyleSheet, useStyles } from 'react-native-unistyles';
import CustomButton from '../../../components/CustomButton';
import { abuseReportService } from '../../../services/central-api/abuse-report';
import Toast from 'react-native-toast-message';
import { ClientError } from '../../../services/central-api/base-service';

interface ModalProps {
    isModalVisible: boolean;
    onClose: () => void;
    abuseReportId?: number;
    description?: string;
    commentary?: string;
    status?: AbuseReportStatus;
    authenticatedUser?: AuthenticatedUser;
}

export default function AbuseReportDetailModal({ isModalVisible, onClose, abuseReportId, description, status, commentary, authenticatedUser }: ModalProps) {
    const { styles } = useStyles(styleSheet);
    const windowInfo = WindowInformation();
    const [commentaryInputValue, setCommentaryInputValue] = useState('');

    useEffect(() => {
        if (isModalVisible) {
            setCommentaryInputValue(commentary ?? '');
        }
    }, [isModalVisible]);

    function isFormValid() {
        return status === 'pending' && commentaryInputValue?.trim().length;
    }

    async function updateAbuseReportStatus(status: AbuseReportStatus) {
        try {
            if (status === 'resolved') {
                await abuseReportService.markAsResolved({ id: abuseReportId!, commentary: commentaryInputValue });
            } else if (status === 'rejected') {
                await abuseReportService.markAsRejected({ id: abuseReportId!, commentary: commentaryInputValue });
            }

            Toast.show({
                type: 'sentinelxSuccess',
                text1: translate('ActionSuccessfully'),
            });

            clearAndClose();
        } catch (err) {
            if (err instanceof ClientError) {
                return Toast.show({
                    type: 'sentinelxError',
                    text1: translate(err.message),
                });
            }

            console.error(err);
            Toast.show({
                type: 'sentinelxError',
                text1: translate('unexpectedError'),
            });
        }
    }

    function clearAndClose() {
        setCommentaryInputValue('');
        onClose();
    }

    return (
        <Modal transparent={true}
            visible={isModalVisible}
            animationType='fade'
            onRequestClose={() => {
                clearAndClose();
            }}
        >
            <View style={styles.container}>
                <View style={[styles.formContainer, windowInfo.isMobile ? { width: '95%' } : { width: '30%' }]}>
                    <View style={{ rowGap: 10 }}>
                        <FormInput
                            label={translate('description')}
                            placeholder={translate('description')}
                            value={description ?? ''}
                            multiline={true}
                            numberOfLines={4}
                            onChangeText={() => { }}
                            disabled={true}
                        />
                        <FormInput
                            label={translate('commentary')}
                            placeholder={translate('commentary')}
                            value={commentaryInputValue ?? ''}
                            multiline={true}
                            numberOfLines={4}
                            onChangeText={setCommentaryInputValue}
                            disabled={status !== 'pending'}
                        />
                    </View>
                    <View style={[styles.buttonContainer]}>
                        <CustomButton
                            onPress={() => clearAndClose()}
                            text={translate('cancel')}
                            type={'secondary'}
                        />
                        {authenticatedUser?.permissions.access_manage_abuse_reports ?
                            <>
                                <CustomButton
                                    onPress={() => updateAbuseReportStatus('rejected')}
                                    text={translate('reject')}
                                    type={'destructive'}
                                    disabled={!isFormValid()}
                                />
                                <CustomButton
                                    onPress={() => updateAbuseReportStatus('resolved')}
                                    text={translate('markAsResolved')}
                                    type={'primary'}
                                    disabled={!isFormValid()}
                                />
                            </>
                            : null}
                    </View>
                </View>
            </View>
        </Modal>

    );
}

const styleSheet = createStyleSheet((theme) => ({
    container: {
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#31313199',
        flex: 1
    },
    formContainer: {
        minWidth: 300,
        minHeight: 150,
        backgroundColor: theme.colors.container.background,
        borderRadius: 4,
        padding: 20,
        rowGap: 25
    },
    buttonContainer: {
        justifyContent: 'center',
        alignItems: 'flex-end',
        flexDirection: 'row',
        flexWrap: 'wrap',
        columnGap: 8,
        rowGap: 8,
        zIndex: -30
    },
}));
